import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { countBy, keyBy, map, set } from 'lodash';
import { customerAPI } from './customerAPI';

export const getCustomer = createAsyncThunk('customers/getSingle', async ({ customerId }) => {
  return customerAPI.getCustomer({ customerId });
});

export const getCustomers = createAsyncThunk('customers/getAll', async (_, { getState }) => {
  const { page, pageSize } = getState().customers;
  const response = await customerAPI.getCustomers({ page, pageSize });

  return {
    customers: response.contents,
    totalCount: response.totalCount,
  };
});

export const getSessionAlerts = createAsyncThunk('customers/sessions/alerts', async () => {
  return (await customerAPI.getSessionAlerts()).sessions;
});

const initialState = {
  customers: {},
  isFetching: false,
  error: null,
  totalCount: 0,
  page: 0, // zero-indexed in state, one-indexed in UI
  pageSize: 20, // hard-coded and can't be changed atm
  sessions: {
    isFetching: false,
    error: null,
    sessionAlerts: {
      pendingCount: null,
      failedCount: null,
      manualControlCount: null,
      errorCount: null,
    },
  },
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    setPage: (state, { payload: page }) => ({ ...state, page }),
    resetError: (state, _) => {
      state.error = null;
    },
    resetCustomerError: (state, { payload: { customerId } }) => {
      set(state.customers[customerId], 'error', null);
    },
    resetSessionsError: (state, _) => {
      state.sessions.error = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(getCustomer.pending, (state, { meta: { arg } }) => {
      set(state.customers[arg.customerId], 'error', null);
    });
    builder.addCase(getCustomer.fulfilled, (state, { payload: customer }) => {
      state.customers[customer.id] = { customer, error: null };
    });
    builder.addCase(getCustomer.rejected, (state, { meta: { arg }, error }) => {
      state.customers[arg.customerId] = { customer: null, error };
    });

    builder.addCase(getCustomers.pending, (state, _) => ({ ...state, isFetching: true, error: null }));
    builder.addCase(getCustomers.fulfilled, (state, { payload }) => ({
      ...state,
      customers: keyBy(
        map(payload.customers, customer => ({ customer, error: null })),
        'customer.id',
      ),
      totalCount: payload.totalCount,
      isFetching: false,
    }));
    builder.addCase(getCustomers.rejected, (state, { error }) => ({ ...state, isFetching: false, error }));

    builder.addCase(getSessionAlerts.pending, (state, _) => {
      state.sessions = {
        ...initialState.sessions,
        isFetching: true,
      };
    });
    builder.addCase(getSessionAlerts.fulfilled, (state, { payload: sessions }) => {
      const alertCount = countBy(sessions, 'status');
      state.sessions = {
        ...state.sessions,
        isFetching: false,
        sessionAlerts: {
          pendingCount: alertCount['PENDING'] || 0,
          failedCount: alertCount['FAILED'] || 0,
          manualControlCount: alertCount['MANUAL_CONTROL'] || 0,
          errorCount: alertCount['ERROR'] || 0,
        },
      };
    });
    builder.addCase(getSessionAlerts.rejected, (state, { error }) => {
      state.sessions = {
        ...initialState.sessions,
        error,
      };
    });
  },
});

export const {
  actions: { setPage, resetError, resetCustomerError, resetSessionsError },
  reducer: customersReducer,
} = customersSlice;
